import { core, payloadToJSON } from '@tock/coretypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

/**
 * workflow state interface, createWorkflow state produces this. it should also be part
 * of the type for your redux slice that is receiving the workflow.
 */
export interface Workflow<T = unknown> {
  workflowInfo?: core.WorkflowInfo;
  data?: T;
  isInitialized: boolean;
  isRequesting: boolean;
  hasError?: boolean;
}

/**
 * creates a workflow state object conforming to the Workflow interface
 */
export function createWorkflowState<T = unknown>(): Workflow<T> {
  return { isInitialized: false, isRequesting: false, data: undefined, workflowInfo: undefined };
}

/**
 * updates the workflow info inside a workflow slice of state
 */
export function handleWorkflowDone<T>(
  workFlowStateSlice: Workflow<T>,
  payload: core.WorkflowInfo
): Workflow<T> {
  return {
    ...workFlowStateSlice,
    workflowInfo: {
      ...payloadToJSON<core.WorkflowInfo>(payload),
    },
  };
}

/**
 * optons object for both the useWorkFlowMonitor hook and the
 * workflow monitor class constructor
 */
export interface WorkflowMonitorOptions<T> {
  /**
   * a function that calls the api handler that returns the initial workflow object.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createWorkflow: (...args: any[]) => Promise<core.WorkflowInfo>;
  /**
   * a function that retrieves the workflow based on workflow id and run id
   */
  getWorkflow: (workflowId: string, runId: string) => Promise<core.WorkflowInfo>;
  /**
   * a function that retrieves the result based on the workflow id and run id
   */
  getResult: (workflow: core.WorkflowInfo) => Promise<T>;
  /**
   * an optional callback that allows you to receive the workflow after it's been initially created
   */
  onCreateWorkflow?: (workflow: core.WorkflowInfo) => void;
  /**
   * an optional callback that allows you to receive updates to the workflow as the monitor is checking
   */
  onGetWorkflow?: (workflow: core.WorkflowInfo) => void;
  /**
   * polling interval in ms, defaults to 250
   */
  pollingInterval?: number;

  /**
   * timeout for polling, in the event that stop is not called properly
   * default is 15 min in ms
   */
  globalTimeout?: number;
}

const errMessage =
  'No workflow present to monitor. Set ensure a workflow is set before calling start';

export class WorkflowMonitor<T> {
  /** the interval id that is returned from setInterval when the workflow is being monitored */
  intervalId: NodeJS.Timeout | undefined;

  /** the time between checking the workflow status */
  pollingInterval = 250;

  /** id that tracks setTimeout for global timeout */
  globalTimeoutId: NodeJS.Timeout | undefined;

  /** if timeout is reached, stop polling */
  globalTimeout = 1000 * 60 * 15;

  /** to prevent race conditions, we set this right before we try to fetch the result so we don't call it again */
  hasCalledResult = false;

  /** the workflow that is created by the createWorkflow function */
  initialWorkflow: core.WorkflowInfo | undefined;

  /** the workflow status at its current state. updated on create and on checking the workflow at given interval */
  currentStatus: core.WorkflowInfo | undefined;

  /** the result of the workflow */
  result: T | undefined;

  /** if an error occurs in the start method, it is set here also for convenience, but will also be in the rejected case of the promise */
  error: Error | undefined;

  /** will be true if for some reason the global timeout was reached */
  hasTimedOut = false;

  /** shortcut for status, accessed via new WorkflowMonitor().status */
  get status() {
    return this.currentStatus ? this.currentStatus : this.initialWorkflow;
  }

  /** function that will be called with the same arugments as start in order to call the handler that creates the initial workflow */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createWorkflow: (...args: any[]) => Promise<core.WorkflowInfo>;

  /** callback for recieving the workflow when it is created */
  onCreateWorkflow: (workflow: core.WorkflowInfo) => void | undefined;

  /** function for getting the workflows status */
  getWorkflow: (workflowId: string, runId: string) => Promise<core.WorkflowInfo>;

  /** callback for receiving the workflow when its status is updated */
  onGetWorkflow: (workflow: core.WorkflowInfo) => void | undefined;

  /** function for getting the workflows results */
  getResult: (workflow: core.WorkflowInfo) => Promise<T>;

  /**
   * takes in a WorkflowMonitorOptions object
   */
  constructor({
    createWorkflow,
    getWorkflow,
    getResult,
    pollingInterval,
    globalTimeout,
    onCreateWorkflow,
    onGetWorkflow,
  }: WorkflowMonitorOptions<T>) {
    this.getWorkflow = getWorkflow;
    this.getResult = getResult;
    this.createWorkflow = createWorkflow;
    if (onCreateWorkflow) {
      this.onCreateWorkflow = onCreateWorkflow;
    }
    if (onGetWorkflow) {
      this.onGetWorkflow = onGetWorkflow;
    }

    if (pollingInterval) {
      this.pollingInterval = pollingInterval;
    }

    if (globalTimeout) {
      this.globalTimeout = globalTimeout;
    }
  }

  /**
   * clear out the values that are used
   * to track monitoring state
   */
  reset() {
    this.hasCalledResult = false;
    this.currentStatus = undefined;
    this.initialWorkflow = undefined;
    this.result = undefined;
    this.error = undefined;
    this.stop();
  }

  /**
   * start monitoring the workflow process. this uses the options for
   * create, get, and get result to run the workflow and check on it until
   * its completed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  start(...args: any[]): Promise<T> {
    // if start is called, make sure to clear out the things that are used in
    // monitoring to re-start it.
    this.reset();
    return new Promise((resolve, reject) => {
      this.createWorkflow(...args)
        .then((initialWorkflow) => {
          this.initialWorkflow = initialWorkflow;
          this.currentStatus = initialWorkflow;
          if (this.onCreateWorkflow) {
            this.onCreateWorkflow(initialWorkflow);
          }
          this.intervalId = undefined;
          if (this.initialWorkflow && this.initialWorkflow.status === 'RUNNING') {
            // start global timeout
            this.globalTimeoutId = setTimeout(() => {
              this.hasTimedOut = true;
              this.stop();
            }, this.globalTimeout);

            this.intervalId = setInterval(async () => {
              if (!this.initialWorkflow) {
                reject(errMessage);
                return;
              }
              try {
                const workflowStatus = await this.getWorkflow(
                  this.initialWorkflow.workflowId,
                  this.initialWorkflow.runId
                );
                this.currentStatus = workflowStatus;
                if (this.onGetWorkflow) {
                  this.onGetWorkflow(workflowStatus);
                }
                if (!this.hasCalledResult && workflowStatus.status !== 'RUNNING') {
                  this.stop();
                  this.hasCalledResult = true;
                  const result = await this.getResult(workflowStatus);
                  this.result = result;
                  resolve(result);
                }
              } catch (error) {
                reject(error);
                this.error = error as Error;
                if (this.intervalId) {
                  clearInterval(this.intervalId);
                }
              }
            }, this.pollingInterval);
          } else if (this.initialWorkflow && this.initialWorkflow.status !== 'RUNNING') {
            this.getResult(this.initialWorkflow)
              .then((value) => {
                resolve(value);
                this.result = value;
              })
              .catch((error) => {
                reject(error);
                this.error = error;
              });
          }
        })
        .catch((error) => {
          reject(error);
          this.error = error;
        });
    });
  }

  /**
   * stops the monitor from polling to get the result
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.globalTimeoutId) {
      clearTimeout(this.globalTimeoutId);
    }
  }
}

interface UseWorkflowMonitorResult<T> {
  result?: T;
  status?: core.WorkflowInfo;
  error?: Error;
}

/**
 * hook interface into the WorkflowMonitor class
 * takes the same options as the constructor. the result is patterned off
 * of react's useState hook. The first value in the array is the result, in the interface of UseWorkflowMonitorResult, and
 * the second value is the start method for starting the workflow, similat to [state, setState] in react's useState hook.
 */
export function useWorkflowMonitor<T>({
  createWorkflow,
  getWorkflow,
  getResult,
  pollingInterval = 250,
  globalTimeout = 1000 * 60 * 15,
  onCreateWorkflow,
  onGetWorkflow,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
WorkflowMonitorOptions<T>): [UseWorkflowMonitorResult<T>, (...args: any[]) => void] {
  const [workflowResult, setWorkflowResult] = useState<UseWorkflowMonitorResult<T>>({});
  const hookOnCreateWorkflow = useCallback(
    (workflow: core.WorkflowInfo) => {
      if (onCreateWorkflow) {
        onCreateWorkflow(workflow);
      }
      setWorkflowResult(() => {
        return {
          status: workflow,
        };
      });
    },
    [onCreateWorkflow]
  );

  const hookOnGetWorkflow = useCallback(
    (workflow: core.WorkflowInfo) => {
      if (onGetWorkflow) {
        onGetWorkflow(workflow);
      }
      setWorkflowResult(() => {
        return {
          status: workflow,
        };
      });
    },
    [onGetWorkflow]
  );

  const wfMonitor = useRef<WorkflowMonitor<T>>(
    new WorkflowMonitor<T>({
      createWorkflow,
      getWorkflow,
      getResult,
      pollingInterval,
      globalTimeout,
      onCreateWorkflow: hookOnCreateWorkflow,
      onGetWorkflow: hookOnGetWorkflow,
    })
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const start = useCallback<(...args: any[]) => void>((...args: []) => {
    wfMonitor.current
      .start(...args)
      .then(() => {
        setWorkflowResult({
          result: wfMonitor.current.result,
          status: wfMonitor.current.status,
        });
      })
      .catch(() => {
        setWorkflowResult({
          error: wfMonitor.current.error,
          status: wfMonitor.current.status,
        });
      });
  }, []);

  useDeepCompareEffect(() => {
    wfMonitor.current.stop();
    wfMonitor.current = new WorkflowMonitor<T>({
      createWorkflow,
      getWorkflow,
      getResult,
      pollingInterval,
      globalTimeout,
      onCreateWorkflow: hookOnCreateWorkflow,
      onGetWorkflow: hookOnGetWorkflow,
    });
  }, [
    getResult,
    getWorkflow,
    pollingInterval,
    createWorkflow,
    hookOnCreateWorkflow,
    hookOnGetWorkflow,
  ]);

  useEffect(() => {
    return () => {
      if (wfMonitor.current) {
        wfMonitor.current.stop();
      }
    };
  }, []);

  return [workflowResult, start];
}
